import { Search, X, Sparkles } from 'lucide-react';
import { clsx } from 'clsx';
import type { ContainerKind } from './SwimLane';

export type OrganizeByMode = 'strategy' | 'campaigns' | 'personas' | 'canonical';

interface OrganizeByOption {
  id: OrganizeByMode;
  label: string;
  emphasis?: 'magic' | 'plain';
}

const ORGANIZE_MODES: OrganizeByOption[] = [
  { id: 'strategy', label: 'Strategy', emphasis: 'magic' },
  { id: 'campaigns', label: 'Campaigns', emphasis: 'plain' },
  { id: 'personas', label: 'Personas', emphasis: 'plain' },
  { id: 'canonical', label: 'Canonical Type', emphasis: 'plain' },
];

interface SwimLaneFilterBarProps {
  // Search/status (existing)
  search: string;
  onSearchChange: (value: string) => void;
  statusFilter: string;
  onStatusFilterChange: (value: string) => void;
  totalShown: number;
  totalAll: number;

  // Container toggle
  container: ContainerKind;
  onContainerChange: (next: ContainerKind) => void;

  // Organize-by mode
  organizeBy: OrganizeByMode;
  onOrganizeByChange: (next: OrganizeByMode) => void;
  isSeed?: boolean;
}

export function SwimLaneFilterBar({
  search,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  totalShown,
  totalAll,
  container,
  onContainerChange,
  organizeBy,
  onOrganizeByChange,
  isSeed,
}: SwimLaneFilterBarProps) {
  const hasFilters = Boolean(search || statusFilter);

  return (
    <div className="space-y-2 py-1">
      {/* ── Primary Search Row ── */}
      <div className="relative group">
        <div className="absolute inset-0 rounded-2xl bg-gradient-to-r from-teal-400/20 to-cyan-400/20 blur-sm opacity-0 group-focus-within:opacity-100 transition-opacity" />
        <div className="relative flex items-center bg-white border border-slate-200 group-focus-within:border-teal-400/60 rounded-2xl shadow-sm transition-all overflow-hidden">
          <Search
            size={16}
            className="absolute left-4 text-slate-400 group-focus-within:text-teal-500 transition-colors pointer-events-none"
          />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder={
              container === 'briefs'
                ? 'Search briefs by name, persona, brief ID…'
                : 'Search creatives by hook, copy, ad ID…'
            }
            className="flex-1 pl-11 pr-4 py-3.5 text-sm bg-transparent placeholder-slate-400 focus:outline-none text-slate-700 min-w-0"
          />
          {search && (
            <button
              type="button"
              onClick={() => onSearchChange('')}
              className="mr-3 text-slate-400 hover:text-slate-600 transition-colors"
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}

          {/* Status select (briefs only) */}
          {container === 'briefs' && (
            <>
              <div className="h-7 w-px bg-slate-100 mr-3" />
              <select
                value={statusFilter}
                onChange={(e) => onStatusFilterChange(e.target.value)}
                className="mr-3 px-2.5 py-1.5 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-100 rounded-lg focus:outline-none focus:ring-1 focus:ring-teal-500/40"
              >
                <option value="">All status</option>
                <option value="draft">Draft</option>
                <option value="active">Active</option>
                <option value="archived">Archived</option>
                <option value="superseded">Superseded</option>
              </select>
            </>
          )}

          {/* Count */}
          <div className="h-7 w-px bg-slate-100 mr-3" />
          <span className="mr-4 text-xs text-slate-400 whitespace-nowrap">
            {totalShown === totalAll
              ? `${totalAll} ${container === 'briefs' ? 'brief' : 'creative'}${
                  totalAll !== 1 ? 's' : ''
                }`
              : `${totalShown} of ${totalAll}`}
          </span>

          {hasFilters && (
            <button
              type="button"
              onClick={() => {
                onSearchChange('');
                onStatusFilterChange('');
              }}
              className="flex items-center gap-1 mr-3 px-2.5 py-1.5 text-xs text-slate-400 hover:text-rose-500 transition-colors rounded-lg hover:bg-rose-50"
            >
              <X size={11} />
              Clear
            </button>
          )}

          {/* Container toggle (right edge) */}
          <div className="hidden md:flex items-center bg-slate-100 rounded-lg p-0.5 mr-3 gap-0.5">
            {(['briefs', 'creatives'] as const).map((opt) => (
              <button
                key={opt}
                type="button"
                onClick={() => onContainerChange(opt)}
                className={clsx(
                  'px-3 py-1 text-[11px] font-semibold capitalize rounded-md transition-all whitespace-nowrap',
                  container === opt
                    ? 'bg-white text-teal-600 shadow-sm'
                    : 'text-slate-500 hover:text-slate-700',
                )}
              >
                {opt === 'briefs' ? 'Briefs' : 'Creatives'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Organize-by chip strip (second row) ── */}
      <div className="flex items-center gap-2 flex-wrap pl-1">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-400">
          Organize by
        </span>
        {ORGANIZE_MODES.map((mode) => {
          const active = organizeBy === mode.id;
          return (
            <button
              key={mode.id}
              type="button"
              onClick={() => onOrganizeByChange(mode.id)}
              className={clsx(
                'inline-flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-full transition-all border',
                active
                  ? mode.emphasis === 'magic'
                    ? 'bg-teal-500 text-white border-teal-500 shadow-sm'
                    : 'bg-navy-900 text-white border-navy-900 shadow-sm'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300 hover:text-teal-600',
              )}
            >
              {mode.emphasis === 'magic' && (
                <Sparkles size={11} className={active ? 'text-white' : 'text-teal-500'} />
              )}
              {mode.label}
              {mode.id === 'strategy' && isSeed && active && (
                <span
                  className="w-1.5 h-1.5 rounded-full bg-amber-400"
                  title="Using seeded clusters — re-run to refresh."
                />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
